import { map } from 'rxjs/operators';
import { BehaviorSubject, fromEvent, merge, Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class NetworkErrorHandlerService {
  private isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  private errorCount = 0;
  private readonly MAX_ERRORS = 5;
  private readonly ERROR_RESET_TIME = 60000; // 1 minute

  constructor() {
    this.setupNetworkMonitoring();
    this.setupErrorCountReset();
  }

  /**
   * Get the current online status
   */
  public getNetworkStatus(): Observable<boolean> {
    return this.isOnline$.asObservable();
  }

  /**
   * Check if the current error should be handled as a network error.
   * Only returns true for errors we can positively identify as network-related.
   * Explicitly excludes HttpErrorResponse with valid status codes (non-zero).
   */
  public shouldHandleError(error: any): boolean {
    if (!error) {
      return false;
    }

    // Don't treat HttpErrorResponse with valid status codes as network errors
    // These are legitimate HTTP responses (like 403, 404, 500) and should be handled as API errors
    if (error instanceof HttpErrorResponse && error.status !== 0) {
      return false;
    }

    // Check constructor name for XMLHttpRequestProgressEvent
    const constructorName = error?.constructor?.name;
    if (constructorName === 'XMLHttpRequestProgressEvent') {
      return true;
    }

    // Check if error.error is a ProgressEvent (common in Angular HTTP errors)
    // But only if it's not an HttpErrorResponse with a valid status
    if (error?.error instanceof ProgressEvent) {
      // Double-check this isn't an HttpErrorResponse with status > 0
      if (error instanceof HttpErrorResponse && error.status !== 0) {
        return false;
      }
      return true;
    }

    // Check if the error itself is a ProgressEvent with type 'error' (indicates network failure)
    if (error instanceof ProgressEvent && error.type === 'error') {
      return true;
    }

    // Check for TypeError with 'Failed to fetch' message (fetch API network errors)
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return true;
    }

    return false;
  }

  /**
   * Handle network-related errors and return appropriate user-friendly messages.
   * Extracts actual error details for better debugging and user feedback.
   */
  public handleNetworkError(error: any): string {
    this.incrementErrorCount();

    // Log actual error details for debugging
    console.warn('NetworkErrorHandler processing error:', {
      constructorName: error?.constructor?.name,
      errorType: error?.type,
      errorMessage: error?.message,
      errorName: error?.name,
      error
    });

    if (!navigator.onLine) {
      return 'No internet connection. Please check your network and try again.';
    }

    if (this.errorCount >= this.MAX_ERRORS) {
      return 'Multiple network errors detected. Please refresh the page or try again later.';
    }

    // Handle XMLHttpRequestProgressEvent
    if (error?.constructor?.name === 'XMLHttpRequestProgressEvent') {
      return 'Network request interrupted. Please try again.';
    }

    // Handle ProgressEvent (usually indicates connection issues)
    if (error?.error instanceof ProgressEvent || error instanceof ProgressEvent) {
      const progressEvent = error instanceof ProgressEvent ? error : error.error;
      if (progressEvent.type === 'error') {
        return 'Unable to connect to the server. Please check your connection and try again.';
      }
      if (progressEvent.type === 'timeout') {
        return 'Request timed out. Please check your connection and try again.';
      }
      return 'Connection issue detected. Please try again.';
    }

    // Handle TypeError with 'Failed to fetch'
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return 'Failed to reach the server. Please check your connection and try again.';
    }

    // Extract any available error information for the fallback
    const errorDetails = this.extractErrorDetails(error);
    if (errorDetails) {
      return `Request failed: ${errorDetails}. Please try again.`;
    }

    // Generic fallback - don't claim it's a "network error" if we don't know
    return 'An unexpected error occurred while processing your request. Please try again.';
  }

  /**
   * Extract meaningful error details from an error object
   */
  private extractErrorDetails(error: any): string | null {
    if (!error) {
      return null;
    }

    // Try to get message from common error properties
    if (typeof error.message === 'string' && error.message.length > 0 && error.message.length < 200) {
      return error.message;
    }

    if (typeof error.statusText === 'string' && error.statusText.length > 0) {
      return error.statusText;
    }

    if (typeof error.name === 'string' && error.name !== 'Error' && error.name.length > 0) {
      return error.name;
    }

    return null;
  }

  /**
   * Get user-friendly error message for XMLHttpRequestProgressEvent
   */
  public getErrorMessage(error: any): string {
    if (this.shouldHandleError(error)) {
      return this.handleNetworkError(error);
    }

    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Check if the application should show a global error banner
   */
  public shouldShowErrorBanner(): boolean {
    return this.errorCount >= 3 || !navigator.onLine;
  }

  /**
   * Reset error count (useful for manual recovery)
   */
  public resetErrorCount(): void {
    this.errorCount = 0;
  }

  private setupNetworkMonitoring(): void {
    // Monitor online/offline events
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).subscribe(isOnline => {
      this.isOnline$.next(isOnline);

      if (isOnline) {
        // Reset error count when connection is restored
        this.resetErrorCount();
      }
    });
  }

  private incrementErrorCount(): void {
    this.errorCount++;
  }

  private setupErrorCountReset(): void {
    // Reset error count every minute
    setInterval(() => {
      if (this.errorCount > 0) {
        this.errorCount = Math.max(0, this.errorCount - 1);
      }
    }, this.ERROR_RESET_TIME);
  }
}
